import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { formatINR, formatDate } from "@/lib/format";
import { Users, Search, Loader2, Plus, Pencil, Ban, ArrowLeft, UserCircle2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  approved_amount: "", interest_rate: "", processing_fee: "", gst: "", tenure: "",
  tenure_unit: "months", repayment_amount: "", disbursement_amount: "",
  repayment_frequency: "one-time", repayment_date: "", late_penalty: "", status: "active",
};
const EDITABLE = ["active", "accepted", "confirmed"];

const STATUS_LABEL = {
  active: "Offer", accepted: "Accepted", confirmed: "Confirmed",
  disbursed: "Disbursed", repaid: "Repaid", cancelled: "Cancelled",
};

const Num = ({ label, k, form, set, type = "number" }) => (
  <div>
    <Label className="mb-1 block text-xs">{label}</Label>
    <Input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`loan-field-${k}`} className="h-10" />
  </div>
);

export default function AdminCustomers() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [dlg, setDlg] = useState(null); // {mode:'add'|'edit', loanId?}
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get("/admin/customers/search", { params: { q: q.trim() } });
      setResults(data);
    } finally { setSearching(false); }
  };

  const openProfile = async (userId) => {
    setLoadingProfile(true);
    try {
      const { data } = await api.get(`/admin/customers/${userId}`);
      setProfile(data);
    } catch (e) {
      toast.error("Could not load customer");
    } finally { setLoadingProfile(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => { setForm(EMPTY); setDlg({ mode: "add" }); };
  const openEdit = (loan) => {
    setForm({
      approved_amount: loan.approved_amount ?? "", interest_rate: loan.interest_rate ?? "",
      processing_fee: loan.processing_fee ?? "", gst: loan.gst ?? "", tenure: loan.tenure ?? "",
      tenure_unit: loan.tenure_unit || "months", repayment_amount: loan.repayment_amount ?? "",
      disbursement_amount: loan.disbursement_amount ?? "", repayment_frequency: loan.repayment_frequency || "one-time",
      repayment_date: loan.repayment_date || "", late_penalty: loan.late_penalty ?? "", status: loan.status,
    });
    setDlg({ mode: "edit", loanId: loan.id });
  };

  const payload = () => ({
    approved_amount: parseFloat(form.approved_amount || 0), interest_rate: parseFloat(form.interest_rate || 0),
    processing_fee: parseFloat(form.processing_fee || 0), gst: parseFloat(form.gst || 0),
    tenure: parseInt(form.tenure || 0, 10), tenure_unit: form.tenure_unit,
    repayment_amount: parseFloat(form.repayment_amount || 0), disbursement_amount: parseFloat(form.disbursement_amount || 0),
    repayment_frequency: form.repayment_frequency, repayment_date: form.repayment_date,
    late_penalty: parseFloat(form.late_penalty || 0), status: form.status,
  });

  const save = async () => {
    if (!form.approved_amount || !form.repayment_amount || !form.disbursement_amount || !form.repayment_date) {
      toast.error("Approved amount, repayment amount, disbursement amount and due date are required");
      return;
    }
    setBusy(true);
    try {
      if (dlg.mode === "add") {
        await api.post(`/admin/customers/${profile.customer.user_id}/loans`, payload());
        toast.success("Loan option added");
      } else {
        await api.put(`/admin/loans/${dlg.loanId}`, payload());
        toast.success("Loan updated");
      }
      setDlg(null);
      await openProfile(profile.customer.user_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save loan");
    } finally { setBusy(false); }
  };

  const cancelLoan = async (loanId) => {
    if (!window.confirm("Cancel this loan option? This cannot be undone.")) return;
    try {
      await api.post(`/admin/loans/${loanId}/cancel`);
      toast.success("Loan cancelled");
      await openProfile(profile.customer.user_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not cancel");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div className="flex items-center gap-2">
        <Users className="h-6 w-6 text-brand" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Customer Loan Management</h1>
          <p className="text-muted-foreground text-sm">Search a customer and assign one or more separate loans.</p>
        </div>
      </div>

      {!profile ? (
        <Card className="p-5 ip-card-shadow">
          <form onSubmit={search} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by Customer ID, Application ID, name or email" className="pl-9" data-testid="customer-search-input" />
            </div>
            <Button type="submit" disabled={searching} data-testid="customer-search-button">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
            </Button>
          </form>

          {results && (
            <div className="mt-4 divide-y divide-border/60">
              {results.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No customers found.</p>}
              {results.map((c) => (
                <button key={c.user_id} onClick={() => openProfile(c.user_id)} className="w-full flex items-center gap-3 py-3 text-left hover:bg-brand-light/40 rounded-lg px-2 -mx-2" data-testid={`customer-result-${c.user_id}`}>
                  <div className="h-10 w-10 grid place-items-center rounded-full bg-brand text-white font-bold shrink-0">{(c.name || "?").charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.email} · {c.phone}</p>
                    <p className="text-[11px] text-muted-foreground font-mono truncate">ID: {c.user_id}</p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground shrink-0">
                    <p>{c.loan_count} loan{c.loan_count === 1 ? "" : "s"}</p>
                    <p>{c.application_count} app{c.application_count === 1 ? "" : "s"}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      ) : loadingProfile ? (
        <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : (
        <>
          <Button variant="ghost" size="sm" onClick={() => setProfile(null)} data-testid="customer-back-button"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to search</Button>

          <Card className="p-5 ip-card-shadow">
            <div className="flex items-center gap-3">
              <UserCircle2 className="h-10 w-10 text-brand" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-lg">{profile.customer.name}</p>
                <p className="text-sm text-muted-foreground truncate">{profile.customer.email} · {profile.customer.phone}</p>
                <p className="text-[11px] text-muted-foreground font-mono truncate">Customer ID: {profile.customer.user_id}</p>
              </div>
              <Button onClick={openAdd} className="shrink-0" data-testid="add-loan-button"><Plus className="h-4 w-4 mr-1.5" /> Add Loan Offer</Button>
            </div>
          </Card>

          <Card className="p-5 ip-card-shadow">
            <h2 className="font-bold mb-3">Loans ({profile.loans.length})</h2>
            {profile.loans.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No loans yet. Use “Add Loan Offer” to create one.</p>
            ) : (
              <div className="space-y-3">
                {profile.loans.map((l) => (
                  <div key={l.id} className="rounded-lg border border-border p-4" data-testid={`admin-loan-${l.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <p className="font-mono text-xs text-muted-foreground">Loan ID: {l.id.slice(0, 12)}</p>
                        <p className="font-mono text-[11px] text-muted-foreground">App: {l.reference_number || "—"}</p>
                      </div>
                      <span className="rounded-full bg-slate-100 text-slate-700 px-2.5 py-0.5 text-xs font-semibold" data-testid={`admin-loan-status-${l.id}`}>{STATUS_LABEL[l.status] || l.status}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <div><p className="text-[11px] text-muted-foreground">Approved</p><p className="font-mono font-semibold">{formatINR(l.approved_amount)}</p></div>
                      <div><p className="text-[11px] text-muted-foreground">Repayment</p><p className="font-mono font-semibold">{formatINR(l.repayment_amount)}</p></div>
                      <div><p className="text-[11px] text-muted-foreground">Outstanding</p><p className="font-mono font-semibold">{formatINR(l.outstanding)}</p></div>
                      <div><p className="text-[11px] text-muted-foreground">Due</p><p className="font-semibold">{l.repayment_date ? formatDate(l.repayment_date) : "—"}</p></div>
                    </div>
                    {EDITABLE.includes(l.status) && (
                      <div className="mt-3 flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(l)} data-testid={`edit-loan-${l.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit</Button>
                        <Button variant="outline" size="sm" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => cancelLoan(l.id)} data-testid={`cancel-loan-${l.id}`}><Ban className="h-3.5 w-3.5 mr-1.5" /> Cancel</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="loan-form-dialog">
          <DialogHeader><DialogTitle>{dlg?.mode === "add" ? "Add Loan Offer" : "Edit Loan"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Num label="Approved amount (₹)" k="approved_amount" form={form} set={set} />
            <Num label="Disbursement amount (₹)" k="disbursement_amount" form={form} set={set} />
            <Num label="Interest rate (%)" k="interest_rate" form={form} set={set} />
            <Num label="Processing fee (₹)" k="processing_fee" form={form} set={set} />
            <Num label="GST (₹)" k="gst" form={form} set={set} />
            <Num label="Late penalty (₹)" k="late_penalty" form={form} set={set} />
            <Num label="Tenure" k="tenure" form={form} set={set} />
            <div>
              <Label className="mb-1 block text-xs">Tenure unit</Label>
              <select value={form.tenure_unit} onChange={(e) => set("tenure_unit", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="loan-field-tenure_unit">
                <option value="days">days</option><option value="weeks">weeks</option><option value="months">months</option>
              </select>
            </div>
            <Num label="Repayment amount (₹)" k="repayment_amount" form={form} set={set} />
            <div>
              <Label className="mb-1 block text-xs">Repayment frequency</Label>
              <select value={form.repayment_frequency} onChange={(e) => set("repayment_frequency", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="loan-field-repayment_frequency">
                <option value="one-time">one-time</option><option value="weekly">weekly</option><option value="monthly">monthly</option>
              </select>
            </div>
            <Num label="Due date" k="repayment_date" form={form} set={set} type="date" />
            {dlg?.mode === "add" && (
              <div>
                <Label className="mb-1 block text-xs">Initial status</Label>
                <select value={form.status} onChange={(e) => set("status", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="loan-field-status">
                  <option value="active">Offer (active)</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="disbursed">Disbursed</option>
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="loan-save-button">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save Loan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
